import { useEffect, useMemo, useState } from "react";
import styles from "./list.module.css";
import dayjs from "dayjs";
import { TextField, Button, Table, TableCell, TableHead, TableContainer, Paper, TableBody, TableRow, TableFooter, Snackbar, Alert, Chip, Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { StyledMainTableCell, StyledMainTableRow } from "../../../components/TableComponent";
import TablePaginationComponent from "../../../components/TablePagination";
import { Close } from "@mui/icons-material";
import axios from "axios";
import Loader from "../../../components/Loader";
import { TableLoader } from "../../../components/ContentLoader";
import { authData, withAdminAuth } from "../../../components/GetAuth";
import { DateRangePicker } from "rsuite";
import download from "downloadjs";
import BookingDetailsPopup from "./bookingDetailsPopup";
import BookingPaymentPopup from "./bookingPaymentPopup";
import { Calendar, Home, Search, X } from "react-feather";
import ActionListItem from "./ActionListItem";
import DeliveryBookingDetailsPopup from "./deliveryBookingDetailsPopup";
import { debounce } from "lodash";

const BookingList = () => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filteredData, setFilteredData] = useState([]);
  const [filterVars, setFilterVars] = useState({ searchKey: "", status: "", date: null });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");
  const [cancelModalOpen, setCancelModalOpen] = useState();
  const [cancelId, setCancelId] = useState(0);
  const [viewModalOpen, setViewModalOpen] = useState();
  const [viewDeliveryModalOpen, setViewDeliveryModalOpen] = useState();
  const [viewId, setViewId] = useState(0);
  const [viewStr, setViewStr] = useState("");
  const [cancelPaymentModalOpen, setCancelPaymentModalOpen] = useState();
  const [cancelPaymentId, setCancelPaymentId] = useState(0);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectBooking, setSelectBooking] = useState(null);
  const [sendInvoiceModalOpen, setSendInvoiceModalOpen] = useState(false);
  const [invoiceMailId, setInvoiceMailId] = useState(0);
  const [invoiceMail, setInvoiceMail] = useState('');
  const [totalCount, setTotalCount] = useState(0);

  const paginationChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginationChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }

  useEffect(() => {
    fetchBookings();
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (page === 0) {
      fetchBookings();
    } else {
      setPage(0);
    }
  }, [filterVars]);

  const debouncedSearch = useMemo(() => debounce((value) => {
    setFilterVars((prev) => ({
      ...prev,
      searchKey: value,
    }));
    setPage(0);
  }, 500), []);

  const fetchBookings = () => {
    axios.get(process.env.REACT_APP_API_URL + "v1/bookings", {
      headers: {
        Authorization: `Bearer ${API_TOKEN}`,
      },
      params: {
        page: page + 1, // Laravel uses 1-based index
        per_page: rowsPerPage,
        search: filterVars.searchKey,
        status: filterVars.status,
        start_date: filterVars.date ? dayjs(filterVars.date[0]).format("YYYY-MM-DD") : null,
        end_date: filterVars.date ? dayjs(filterVars.date[1]).format("YYYY-MM-DD") : null,
      }
    }).then((response) => {
      setContentLoading(false);

      setFilteredData(response.data.data.data); // actual rows
      setTotalCount(response.data.data.total); // total rows
    }).catch(() => {
      setContentLoading(false);
    });
  }

  const cancelBooking = () => {
    setLoading(true);
    setCancelModalOpen(false);

    axios.put(process.env.REACT_APP_API_URL + "v1/bookings/" + cancelId, { status: -1 }, {
      headers: {
        Authorization: `Bearer ${API_TOKEN}`,
      }
    }).then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") {
        setSnackbarOpen(true);
        setSnackbarMessage("Booking cancelled successfully.");
        setSnackbarType("success");

        setFilteredData((prev) => {
          return prev.map((i) => {
            if (i?.id === cancelId) {
              return { ...i, status: -1 };
            }
            return i;
          });
        });

        setCancelId(0);
      }
    }).catch((error) => {
      setLoading(false);
    });
  };

  const printBooking = (id) => {
    setLoading(true);
    axios(process.env.REACT_APP_API_URL + "v1/bookings-item-pdf/" + id, {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${API_TOKEN}`,
      }
    }).then((response) => {
      setLoading(false);
      const content = response.headers["content-type"];
      download(response.data, "booking-" + id + ".pdf", content);
    }).catch((error) => {
      setLoading(false);
    });
  };

  const cancelPayment = () => {
    setLoading(true);
    setCancelPaymentModalOpen(false);

    axios.put(process.env.REACT_APP_API_URL + "v1/bookings/" + cancelPaymentId, { note: "", payment_status: 0 }, {
      headers: {
        Authorization: `Bearer ${API_TOKEN}`,
      }
    }).then((response) => {
      setLoading(false);

      if (response.data.status === "SUCCESS") {
        setSnackbarOpen(true);
        setSnackbarMessage(response.data.message);
        setSnackbarType("success");

        setFilteredData((prev) => {
          return prev.map((i) => {
            if (i?.id === cancelPaymentId) {
              return { ...i, payment_status: 0 };
            }
            return i;
          });
        });

        setCancelPaymentId(0);
      }
    }).catch((error) => {
      setLoading(false);
    });
  };

  const paymentItem = (response) => {
    setLoading(false);
    setPaymentModalOpen(false);
    setSnackbarOpen(true);
    setSnackbarMessage(response.message);
    setSnackbarType("success");

    setFilteredData((prev) => {
      return prev.map((i) => {
        if (i?.id === response.data.id) {
          return { ...i, payment_status: response.data.payment_status, paid_amount: response.data.paid_amount, due_balance: response.data.due_balance };
        }
        return i;
      });
    });

    setSelectBooking(null);
  }

  const sendMailBooking = () => {
    setSendInvoiceModalOpen(false);
    setLoading(true);
    axios(process.env.REACT_APP_API_URL + 'v1/send-booking-pdf/' + invoiceMailId, {
      headers: {
        Authorization: `Bearer ${API_TOKEN}`,
      }
    }).then(response => {
      if (response.data.status === 'SUCCESS') {
        setLoading(false);
        setSnackbarOpen(true);
        setSnackbarMessage(response.data.message);
        setSnackbarType("success");
      } else {
        setLoading(false);
      }
    }).catch(error => {
      setLoading(false);
    });
  }

  return (<>
    <Loader open={loading} setOpen={setLoading.bind(this)} />
    <div className={`${styles.MainDiv}`}>
      <div className={`${styles.MainCard}`}>
        {contentLoading && <TableLoader />}

        {!contentLoading && (<div className={`${styles.PageTitleWrap}`}>
          <p className={`${styles.PageTitle}`}>Booking List</p>
          <nav aria-label="breadcrumb">
            <ol className={`${styles.breadcrumb}`}>
              <li className={`${styles.breadcrumbItem}`}><span><Home /></span>Booking</li>
              <li className={`${styles.breadcrumbItem} ${styles.active}`}>List</li>
            </ol>
          </nav>
        </div>)}

        {!contentLoading && (<>
          <div className="TableFilterHead">
            <div className="DataTableSearch WithFilter">
              <Search className="SearchIcon" />
              <TextField type="text" placeholder="Search" value={filterVars?.searchKey} onChange={(e) => debouncedSearch(e.target.value)} />
              {filterVars?.searchKey !== "" && (<Button type="button" className="CloseBU" onClick={(e) => setFilterVars((prev) => { return { ...prev, searchKey: "" }; })}><Close /></Button>)}
            </div>
            <div className="TableFilterBUSec">
              <div className={`${styles.FilterCard} ${styles.Select} CusSelectFilter Filter`}>
                <FormControl fullWidth>
                  <InputLabel>Filter By Status</InputLabel>
                  <Select value={filterVars?.status} label="Filter By Status" onChange={(e) => setFilterVars((prev) => { return { ...prev, status: e.target.value }; })}>
                    <MenuItem value={"all"}>All</MenuItem>
                    <MenuItem value={-1}>Order Cancelled - UK</MenuItem>
                    <MenuItem value={0}>Order Booked - UK</MenuItem>
                    <MenuItem value={1}>Item Assigned To Driver - UK</MenuItem>
                    <MenuItem value={2}>Item Picked Up - UK</MenuItem>
                    <MenuItem value={3}>Item In Warehouse - UK</MenuItem>
                    <MenuItem value={4}>Item Shipped From Warehouse - UK</MenuItem>
                    <MenuItem value={5}>Item Received In Warehouse - Ghana</MenuItem>
                    <MenuItem value={6}>Item Out For Delivery - Ghana</MenuItem>
                    <MenuItem value={7}>Delivered - Ghana</MenuItem>
                  </Select>
                </FormControl>
              </div>
              <div className={`${styles.FilterCard} Filter`}>
                <div className={`${styles.DashFilterSec} DashFilterSec`}>
                  <DateRangePicker format={"dd/MM/yyyy"} placement={"bottomEnd"} placeholder="Serach by date" caretAs={Calendar} value={filterVars?.date} showOneCalendar onChange={(e) => setFilterVars((prev) => { return { ...prev, date: e }; })} />
                </div>
              </div>
            </div>
          </div>
          <div className={`${styles.CoutryTagSec}`}>
            <div className={`${styles.CoutryTag}`}>
              <span className={`${styles.TagColor}`} style={{ background: "#FFFFFF", border: "#000 1px solid" }}></span>
              <span>Booked - UK</span>
            </div>
            <div className={`${styles.CoutryTag}`}>
              <span className={`${styles.TagColor}`} style={{ background: "rgba(255,0,0,0.7)" }}></span>
              <span>Cancelled - UK</span>
            </div>
            <div className={`${styles.CoutryTag}`}>
              <span className={`${styles.TagColor}`} style={{ background: "rgba(230, 79, 34, 0.1)" }}></span>
              <span>Assigned To Driver - UK</span>
            </div>
            <div className={`${styles.CoutryTag}`}>
              <span className={`${styles.TagColor}`} style={{ background: "rgba(230, 79, 34, 0.4)" }}></span>
              <span>Picked Up - UK</span>
            </div>
            <div className={`${styles.CoutryTag}`}>
              <span className={`${styles.TagColor}`} style={{ background: "rgba(230, 79, 34, 0.7)" }}></span>
              <span>In Warehouse - UK</span>
            </div>
            <div className={`${styles.CoutryTag}`}>
              <span className={`${styles.TagColor}`} style={{ background: "rgba(42, 146, 245, 0.4)" }}></span>
              <span>Shipped From Warehouse - UK</span>
            </div>
            <div className={`${styles.CoutryTag}`}>
              <span className={`${styles.TagColor}`} style={{ background: "rgba(42, 146, 245, 0.5)" }}></span>
              <span>Received In Warehouse - Ghana</span>
            </div>
            <div className={`${styles.CoutryTag}`}>
              <span className={`${styles.TagColor}`} style={{ background: "rgba(5, 156, 42, 0.5)" }}></span>
              <span>Out For Delivery - Ghana</span>
            </div>
            <div className={`${styles.CoutryTag}`}>
              <span className={`${styles.TagColor}`} style={{ background: "rgba(5, 156, 42, 0.9)" }}></span>
              <span>Delivered - Ghana</span>
            </div>
          </div>

          <div className="CusTable">
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <StyledMainTableCell>Booking Id</StyledMainTableCell>
                    <StyledMainTableCell>Customer Name</StyledMainTableCell>
                    <StyledMainTableCell>Order Date</StyledMainTableCell>
                    <StyledMainTableCell>Total Amount</StyledMainTableCell>
                    <StyledMainTableCell>Paid Amount</StyledMainTableCell>
                    <StyledMainTableCell>Due Balance</StyledMainTableCell>
                    <StyledMainTableCell>Order Status</StyledMainTableCell>
                    <StyledMainTableCell>Pickup Post Code</StyledMainTableCell>
                    <StyledMainTableCell>Pickup Date</StyledMainTableCell>
                    <StyledMainTableCell>Created User</StyledMainTableCell>
                    <StyledMainTableCell>Status</StyledMainTableCell>
                    <StyledMainTableCell></StyledMainTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredData.map((item) => {
                    let backGround = "rgb(255,255,255)";
                    if (item?.status === -1 || item?.status === "-1") {
                      backGround = "rgba(255,0,0,0.7)";
                    }
                    if (item?.status === 1 || item?.status === "1") {
                      backGround = "rgba(230, 79, 34, 0.1)";
                    }
                    if (item?.status === 2 || item?.status === "2") {
                      backGround = "rgba(230, 79, 34, 0.4)";
                    }
                    if (item?.status === 3 || item?.status === "3") {
                      backGround = "rgba(230, 79, 34, 0.7)";
                    }
                    if (item?.status === 4 || item?.status === "4") {
                      backGround = "rgba(42, 146, 245, 0.4)";
                    }
                    if (item?.status === 5 || item?.status === "5") {
                      backGround = "rgba(42, 146, 245, 0.7)";
                    }
                    if (item?.status === 6 || item?.status === "6") {
                      backGround = "rgba(5, 156, 42, 0.5)";
                    }
                    if (item?.status === 7 || item?.status === "7") {
                      backGround = "rgba(5, 156, 42, 0.9)";
                    }

                    return (<StyledMainTableRow key={item?.id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }} style={{ background: `${backGround}` }}>
                      <TableCell component="th" scope="row">{item?.booking_id}</TableCell>
                      <TableCell component="th" scope="row">{item?.customer_name}</TableCell>
                      <TableCell component="th" scope="row">{dayjs(item?.created_at).format("DD/MM/YYYY")}</TableCell>
                      <TableCell component="th" scope="row">£{parseFloat(item?.total).toFixed(2)}</TableCell>
                      <TableCell component="th" scope="row">£{parseFloat(item?.paid_amount).toFixed(2)}</TableCell>
                      <TableCell component="th" scope="row">£{parseFloat(item?.due_balance).toFixed(2)}</TableCell>
                      <TableCell>
                        {(auth?.user_type === 1 || auth?.user_type === 2 || auth?.permissions?.indexOf('bookings:update') > -1) ? (item?.status === -1 || item?.status === "-1" ? (<Button className={`${styles.StatusBU} ${styles.DisaBU}`}>Cancel</Button>) : item?.payment_status === 1 || item?.payment_status === "1" ? (<Button className={`${styles.StatusBU} ${styles.DisaBU2}`} onClick={(e) => { setCancelPaymentModalOpen(true); setCancelPaymentId(item?.id); }}>Paid</Button>) : (<Button className={`${styles.StatusBU}`} onClick={(e) => { setPaymentModalOpen(true); setSelectBooking(item); }}>Unpaid</Button>)) : (item?.status === -1 || item?.status === "-1" ? (<Button className={`${styles.StatusBU} ${styles.DisaBU}`}>Cancel</Button>) : item?.payment_status === 1 || item?.payment_status === "1" ? (<Button className={`${styles.StatusBU}}`}>Paid</Button>) : (<Button className={`${styles.StatusBU}`}>Unpaid</Button>))}
                      </TableCell>
                      <TableCell component="th" scope="row">{item?.post_code}</TableCell>
                      <TableCell component="th" scope="row">{dayjs(item?.pickup_date).format("DD/MM/YYYY")}</TableCell>
                      <TableCell component="th" scope="row">{item?.created_user}</TableCell>
                      <TableCell component="th" scope="row">
                        {parseInt(item?.status) === -1 && (<Chip label="Cancelled" variant="outlined" style={{ fontWeight: "600", backgroundColor: "#FF4D4D", borderColor: "#FF4D4D", color: "#FFFFFF" }} />)}
                        {parseInt(item?.status) === 0 && (<Chip label="Booked" variant="outlined" style={{ fontWeight: "600", backgroundColor: "#FFFFFF", borderColor: "#8C8C8C", color: "#8C8C8C" }} />)}
                        {parseInt(item?.status) === 1 && (<Chip label="Assigned To Driver" variant="outlined" style={{ fontWeight: "600", backgroundColor: "#FDEDE9", borderColor: "#ef233c", color: "#ef233c" }} />)}
                        {parseInt(item?.status) === 2 && (<Chip label="Item Picked Up" variant="outlined" style={{ fontWeight: "600", backgroundColor: "#FDEDE9", borderColor: "#ef233c", color: "#ef233c" }} />)}
                        {parseInt(item?.status) === 3 && (<Chip label="Item In Warehouse" variant="outlined" style={{ fontWeight: "600", backgroundColor: "#FDEDE9", borderColor: "#ef233c", color: "#ef233c" }} />)}
                        {parseInt(item?.status) === 4 && (<Chip label="Shipped From Warehouse" variant="outlined" style={{ fontWeight: "600", backgroundColor: "#FDEDE9", borderColor: "#ef233c", color: "#ef233c" }} />)}
                        {parseInt(item?.status) === 5 && (<Chip label="Received In GH Warehouse" variant="outlined" style={{ fontWeight: "600", backgroundColor: "#FDEDE9", borderColor: "#ef233c", color: "#ef233c" }} />)}
                        {parseInt(item?.status) === 6 && (<Chip label="Out For Delivery" variant="outlined" style={{ fontWeight: "600", backgroundColor: "#FDEDE9", borderColor: "#ef233c", color: "#ef233c" }} />)}
                        {parseInt(item?.status) === 7 && (<Chip label="Delivered" variant="outlined" style={{ fontWeight: "600", backgroundColor: "#FDEDE9", borderColor: "#ef233c", color: "#ef233c" }} />)}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <ActionListItem item={item} setInvoiceMail={setInvoiceMail.bind(this)} setInvoiceMailId={setInvoiceMailId.bind(this)} setSendInvoiceModalOpen={setSendInvoiceModalOpen.bind(this)} printBooking={printBooking.bind(this)} setCancelId={setCancelId.bind(this)} setCancelModalOpen={setCancelModalOpen.bind(this)} setViewStr={setViewStr.bind(this)} setViewId={setViewId.bind(this)} setViewModalOpen={setViewModalOpen.bind(this)} setViewDeliveryModalOpen={setViewDeliveryModalOpen.bind(this)} />
                      </TableCell>
                    </StyledMainTableRow>);
                  })}

                  {filteredData.length === 0 && (<TableRow>
                    <TableCell colSpan={10} align="center">No data found</TableCell>
                  </TableRow>)}
                </TableBody>

                {totalCount > 0 && (<TableFooter>
                  <TableRow>
                    <TablePaginationComponent
                      count={totalCount}
                      colSpan={6}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      handleChangePage={paginationChangePage}
                      handleChangeRowsPerPage={paginationChangeRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>)}
              </Table>
            </TableContainer>
          </div>
        </>)}
      </div>
    </div>

    <Dialog
      fullWidth
      maxWidth="md"
      className="NewPopUp"
      open={viewModalOpen}
      onClose={(e) => setViewModalOpen(false)}
    >
      <DialogTitle id="alert-dialog-title">
        Order Information: #{viewStr}
        <button onClick={(e) => setViewModalOpen(false)}><X /></button>
      </DialogTitle>
      <DialogContent>
        <BookingDetailsPopup bookingId={viewId} />
      </DialogContent>
    </Dialog>

    <Dialog
      fullWidth
      maxWidth="md"
      className="NewPopUp"
      open={viewDeliveryModalOpen}
      onClose={(e) => setViewDeliveryModalOpen(false)}
    >
      <DialogTitle id="alert-dialog-title">
        Order Information: #{viewStr}
        <button onClick={(e) => setViewDeliveryModalOpen(false)}><X /></button>
      </DialogTitle>
      <DialogContent>
        <DeliveryBookingDetailsPopup bookingId={viewId} />
      </DialogContent>
    </Dialog>

    <Dialog
      open={cancelModalOpen}
      onClose={(e) => setCancelModalOpen(false)}
      fullWidth
      maxWidth="xs"
      className="NewPopUp"
    >
      <DialogTitle id="alert-dialog-title">
        {"Cancel Booking?"}
        <button onClick={(e) => setCancelModalOpen(false)}><X /></button>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <div className="ModalFormGroup">
            <label className="PopupBodyText">Are you sure to cancel this item?</label>
          </div>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button className="CancelPopupBU" onClick={(e) => setCancelModalOpen(false)}>Cancel</Button>
        <Button className="SubmitPopupBU" onClick={(e) => cancelBooking()}>Confirm</Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={sendInvoiceModalOpen}
      onClose={(e) => setSendInvoiceModalOpen(false)}
      fullWidth
      maxWidth="xs"
      className="NewPopUp"
    >
      <DialogTitle id="alert-dialog-title">
        {"Send Invoice?"}
        <button onClick={(e) => setSendInvoiceModalOpen(false)}><X /></button>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <div className="ModalFormGroup">
            <label className="PopupBodyText">Are you sure to send invoice to <strong>{invoiceMail}</strong>?</label>
          </div>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button className="CancelPopupBU" onClick={(e) => setSendInvoiceModalOpen(false)}>Cancel</Button>
        <Button className="SubmitPopupBU" onClick={(e) => sendMailBooking()}>Confirm</Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={cancelPaymentModalOpen}
      onClose={(e) => setCancelPaymentModalOpen(false)}
      fullWidth
      maxWidth="xs"
      className="NewPopUp"
    >
      <DialogTitle id="alert-dialog-title">
        {"Cancel Payment?"}
        <button onClick={(e) => setCancelPaymentModalOpen(false)}><X /> </button>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <div className="ModalFormGroup">
            <label className="PopupBodyText">Do you want to cancal payment now?</label>
          </div>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button className="CancelPopupBU" onClick={(e) => setCancelPaymentModalOpen(false)}>No</Button>
        <Button className="SubmitPopupBU" onClick={(e) => cancelPayment()}>Yes</Button>
      </DialogActions>
    </Dialog>

    <Dialog
      fullWidth
      maxWidth="sm"
      className="NewPopUp"
      open={paymentModalOpen}
      onClose={(e) => setPaymentModalOpen(false)}
    >
      <DialogTitle id="alert-dialog-title">
        Payment
        <button onClick={(e) => setPaymentModalOpen(false)}><X /></button>
      </DialogTitle>
      <DialogContent>
        <BookingPaymentPopup modalClose={setPaymentModalOpen.bind(this)} paymentItem={paymentItem.bind(this)} selectBooking={selectBooking} />
      </DialogContent>
    </Dialog>

    <Snackbar
      open={snackbarOpen}
      autoHideDuration={6000}
      onClose={(e) => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Alert
        onClose={(e) => setSnackbarOpen(false)}
        severity={snackbarType}
        variant="filled"
        sx={{ width: "100%" }}
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
  </>);
};

export default withAdminAuth(BookingList);